import React from 'react';
import dayjs from 'dayjs';
import BedtimeRounded from '@mui/icons-material/BedtimeRounded';
import Notice from 'components/common/Notice/Notice';
import { useGetTrailWeatherQuery } from 'app/api/trailApiSlice';

const TWELVE_HOURS_MINUTES = 12 * 60;

const moon = <BedtimeRounded className="ts-notice__icon" aria-hidden="true" />;

// Guidance-only, same as the weather warning banner in WeatherForecast (see
// ADR-0007) — this never gates or disables Start Activity, it just informs
// the walker before they decide. Only single-day walks under 12 hours are
// checked; anything longer is assumed to be a multi-day trip where "before
// dark" isn't the relevant question. Two distinct warnings: it's already
// past sunset right now, or starting now would still have you out past
// sunset by the time you finish.
const DarkFinishWarning = ({ trailId, durationMinutes }) => {
  const { data, isLoading } = useGetTrailWeatherQuery(trailId, {
    skip: !trailId,
  });

  if (
    isLoading ||
    !data ||
    data.hasCoordinates === false ||
    data.success === false
  ) {
    return null;
  }

  if (durationMinutes >= TWELVE_HOURS_MINUTES) {
    return null;
  }

  const sunset = dayjs(data.days?.[0]?.sunsetTime);
  const now = dayjs();

  if (!sunset.isValid()) {
    return null;
  }

  if (now.isAfter(sunset)) {
    return (
      <Notice tone="warning" icon={moon} title="It's already dark">
        Sunset was earlier today. Walking at night is more dangerous. Only very
        experienced walkers should start now.
      </Notice>
    );
  }

  const estimatedFinish = now.add(durationMinutes, 'minute');
  if (!estimatedFinish.isAfter(sunset)) {
    return null;
  }

  return (
    <Notice tone="warning" icon={moon} title="You may finish after dark">
      If you start now, you should finish at about{' '}
      <strong>{estimatedFinish.format('h:mm A')}</strong>. Sunset is at{' '}
      <strong>{sunset.format('h:mm A')}</strong>. Start earlier, or pack a torch
      and warm layers.
    </Notice>
  );
};

export default DarkFinishWarning;
